import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import "./style.css";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";
import * as amplitude from "@amplitude/analytics-browser";
import en from "./translations/en.json";
import de from "./translations/de.json";
import es from "./translations/es.json";
import fa from "./translations/fa.json";
import fr from "./translations/fr.json";
import hi from "./translations/hi-IN.json";
import id from "./translations/id.json";
import it from "./translations/it.json";
import ja from "./translations/ja.json";
import ko from "./translations/ko.json";
import nl from "./translations/nl.json";
import th from "./translations/th.json";
import zh from "./translations/zh.json";
import zhTW from "./translations/zh-TW.json";
import zhHK from "./translations/zh-HK.json";
import zhMO from "./translations/zh-MO.json";
import vi from "./translations/vi.json";
import { applicationState } from "./utils/globalReferences.js";
import { initialLanguage } from "./utils/language.js";

// import for side effects only
import "./bootstrap/canary.js";

const app = createApp(App);

if (import.meta.env.MODE === "staging" || import.meta.env.MODE === "development") {
  import("eruda").then((module) => {
    module.default.init();
  });
}

const i18n = createI18n({
  legacy: false,
  locale: initialLanguage,
  fallbackLocale: initialLanguage,
  // hk and mo are needed for proper camera selection
  messages: { en, de, es, fa, fr, hi, id, it, ja, ko, nl, th, zh, "zh-TW": zhTW, "zh-HK": zhHK, "zh-MO": zhMO, vi }
});

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: [Sentry.browserTracingIntegration(), Sentry.httpClientIntegration()],
    sendDefaultPii: true, // required for httpClientIntegration
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^https:\/\/api\.scantrust\.com/, // this backend support sentry-trace header
      /^https:\/\/api\.staging.scantrust\.io/ // this backend support sentry-trace header
    ]
  });
}

if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
  const currentHost = window.location.hostname;
  const reducedSampleRateHosts = import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE_HOSTS?.split(",") || [];
  // The env files hold the only copy of this rate: 0 is honored, and anything unusable means no reduction.
  const reducedSampleRate = parseFloat(import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE);
  const sampleRate = reducedSampleRateHosts.includes(currentHost) && reducedSampleRate >= 0 && reducedSampleRate <= 1 ? reducedSampleRate : 1;

  amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
    defaultTracking: false,
    useDynamicConfig: true,
    sampleRate: sampleRate
  });
}

app.use(i18n);
app.mount("#app");
